/**
 * Tenants Hooks - TanStack Query implementation
 *
 * Server state management for tenant data.
 * Follows MaldevtaHub architectural principles.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api-client'
import { queryKeys, invalidateQueries } from '@/lib/query-keys'
import { toast } from 'sonner'
import type { Tenant } from '@/lib/types'

// Types
export interface CreateTenantData {
  name: string
  domain?: string
  licenseId?: number
}

export interface UpdateTenantData {
  name?: string
  domain?: string
  licenseId?: number
}

// ============================================================================
// QUERIES
// ============================================================================

/**
 * Fetch all tenants with pagination
 */
export function useTenants(params?: { page?: number; limit?: number }) {
  return useQuery({
    queryKey: [...queryKeys.tenants.lists(), params] as const,
    queryFn: async () => {
      const response = await api.listTenants(params)
      // Return full response structure for pagination metadata
      return response
    },
    staleTime: 0, // Disable caching for dev
    gcTime: 10 * 60 * 1000, // 10 minutes
  })
}

/**
 * Fetch tenant detail by ID
 */
export function useTenant(id: number | null | undefined) {
  return useQuery({
    queryKey: id ? queryKeys.tenants.detail(id) : ['tenant', 'null'],
    queryFn: async () => {
      if (!id) return null
      const response = await api.getTenant(id)
      return response.data || null
    },
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
  })
}

/**
 * Fetch tenant limits
 */
export function useTenantLimits(tenantId: number) {
  return useQuery({
    queryKey: ['tenant', tenantId, 'limits'] as const,
    queryFn: async () => {
      const response = await api.getTenantLimits(tenantId)
      return response.data
    },
    enabled: !!tenantId,
    staleTime: 5 * 60 * 1000,
  })
}

/**
 * Check if tenant is within limits
 */
export function useCheckTenantLimits(tenantId: number) {
  return useQuery({
    queryKey: ['tenant', tenantId, 'limits-check'] as const,
    queryFn: async () => {
      const response = await api.checkTenantLimits(tenantId)
      return response.data
    },
    enabled: !!tenantId,
    staleTime: 2 * 60 * 1000, // 2 minutes for limit checks
    refetchInterval: 30 * 1000, // Check every 30s
  })
}

// ============================================================================
// MUTATIONS
// ============================================================================

/**
 * Create new tenant
 */
export function useCreateTenant() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: CreateTenantData) => api.createTenant(data),

    onSuccess: () => {
      invalidateQueries(queryClient, 'tenants')
      toast.success('Tenant created successfully')
    },

    onError: (error: any) => {
      toast.error(error?.message || 'Failed to create tenant')
    },
  })
}

/**
 * Update tenant
 */
export function useUpdateTenant() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdateTenantData }) =>
      api.updateTenant(id, data),

    onMutate: async ({ id, data }) => {
      // Cancel ongoing queries
      await queryClient.cancelQueries({ queryKey: queryKeys.tenants.lists() })

      // Snapshot previous value for rollback
      const previous = queryClient.getQueryData(queryKeys.tenants.lists())

      // Optimistic update
      queryClient.setQueryData(
        queryKeys.tenants.lists(),
        (old: Tenant[] | undefined) =>
          old?.map(tenant =>
            tenant.id === id ? { ...tenant, ...data } : tenant
          )
      )

      return { previous }
    },

    onError: (err, _variables, context) => {
      // Rollback on error
      if (context?.previous) {
        queryClient.setQueryData(queryKeys.tenants.lists(), context.previous)
      }
      toast.error(err?.message || 'Failed to update tenant')
    },

    onSettled: () => {
      // Always invalidate after mutation
      invalidateQueries(queryClient, 'tenants')
    },
  })
}

/**
 * Suspend tenant
 */
export function useSuspendTenant() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: number) => api.suspendTenant(id),

    onSuccess: () => {
      invalidateQueries(queryClient, 'tenants')
      toast.success('Tenant suspended')
    },

    onError: (error: any) => {
      toast.error(error?.message || 'Failed to suspend tenant')
    },
  })
}

/**
 * Activate tenant
 */
export function useActivateTenant() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: number) => api.activateTenant(id),

    onSuccess: () => {
      invalidateQueries(queryClient, 'tenants')
      toast.success('Tenant activated')
    },

    onError: (error: any) => {
      toast.error(error?.message || 'Failed to activate tenant')
    },
  })
}

/**
 * Delete tenant
 */
export function useDeleteTenant() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: number) => api.deleteTenant(id),

    onSuccess: () => {
      invalidateQueries(queryClient, 'tenants')
      toast.success('Tenant deleted')
    },

    onError: (error: any) => {
      toast.error(error?.message || 'Failed to delete tenant')
    },
  })
}

// ============================================================================
// UTILITY HOOKS
// ============================================================================

/**
 * Hook to refetch all tenant queries
 */
export function useRefetchTenants() {
  const queryClient = useQueryClient()

  return () => {
    queryClient.invalidateQueries({
      queryKey: queryKeys.tenants.root,
    })
  }
}
